/**
 * FSRS Helper - Wrapper for ts-fsrs library
 * Provides spaced repetition scheduling for flashcards
 */
import { MS_PER_MINUTE, MS_PER_HOUR, MS_PER_DAY } from '../constants/time';

import { 
  createEmptyCard, 
  fsrs, 
  generatorParameters, 
  Rating,
  State
} from 'ts-fsrs';

// FSRS instance with default parameters
const params = generatorParameters({ 
  enable_fuzz: true,  // Add small random fuzz to intervals
  enable_short_term: true,  // Enable short-term learning steps
});
const f = fsrs(params);

// Export Rating and State enums for use in components
export { Rating, State };

// Rating labels for UI
export const RATING_LABELS = {
  [Rating.Again]: 'Again',
  [Rating.Hard]: 'Hard', 
  [Rating.Good]: 'Good',
  [Rating.Easy]: 'Easy',
};

// State labels for UI
export const STATE_LABELS = {
  [State.New]: 'New',
  [State.Learning]: 'Learning',
  [State.Review]: 'Review',
  [State.Relearning]: 'Relearning',
};

/**
 * Create a new empty card with FSRS default values
 * @param {Date} now - Optional current date
 * @returns {object} Empty card object
 */
export const createNewCard = (now = new Date()) => {
  const card = createEmptyCard(now);
  return {
    due: card.due.toISOString(),
    stability: card.stability,
    difficulty: card.difficulty,
    elapsed_days: card.elapsed_days,
    scheduled_days: card.scheduled_days,
    reps: card.reps,
    lapses: card.lapses,
    state: card.state,
    last_review: card.last_review ? card.last_review.toISOString() : null,
  };
};

/**
 * Get scheduling options for all ratings
 * @param {object} card - Card object from database
 * @param {Date} now - Current date
 * @returns {object} Scheduling options for each rating
 */
export const getSchedulingOptions = (card, now = new Date()) => {
  // Convert DB card to ts-fsrs Card format
  const fsrsCard = {
    due: new Date(card.due),
    stability: card.stability,
    difficulty: card.difficulty,
    elapsed_days: card.elapsed_days,
    scheduled_days: card.scheduled_days,
    reps: card.reps,
    lapses: card.lapses,
    state: card.state,
    last_review: card.last_review ? new Date(card.last_review) : undefined,
  };

  // Get scheduling for all ratings
  const scheduling = f.repeat(fsrsCard, now);
  
  // Return formatted options with intervals
  return {
    [Rating.Again]: formatSchedulingOption(scheduling[Rating.Again], now),
    [Rating.Hard]: formatSchedulingOption(scheduling[Rating.Hard], now),
    [Rating.Good]: formatSchedulingOption(scheduling[Rating.Good], now),
    [Rating.Easy]: formatSchedulingOption(scheduling[Rating.Easy], now),
  };
};

/**
 * Format a scheduling option for display
 */
const formatSchedulingOption = (option, now) => {
  const { card, log } = option;
  const interval = getIntervalDisplay(card.due, now);
  
  return {
    card: {
      due: card.due.toISOString(),
      stability: card.stability,
      difficulty: card.difficulty,
      elapsed_days: card.elapsed_days,
      scheduled_days: card.scheduled_days,
      reps: card.reps,
      lapses: card.lapses,
      state: card.state,
      last_review: now.toISOString(),
    },
    log,
    interval,
  };
};

/**
 * Format interval for display (e.g., "<1min", "10min", "4d")
 */
export const getIntervalDisplay = (dueDate, now) => { // EXPORTED_FOR_TESTING
  const diffMs = dueDate - now;
  const diffMins = Math.round(diffMs / MS_PER_MINUTE);
  const diffHours = Math.round(diffMs / MS_PER_HOUR);
  const diffDays = Math.round(diffMs / MS_PER_DAY);

  if (diffMs < 60000) return '<1min';
  if (diffMins < 60) return `${diffMins}min`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 30) return `${diffDays}d`;
  if (diffDays < 365) return `${Math.round(diffDays / 30)}mo`;
  return `${Math.round(diffDays / 365)}y`;
};

/**
 * Lapses at which the lamp is fully red on its own, whatever FSRS thinks of the
 * card. Anki suspends a leech at 8; this is the point where a card is visibly
 * fighting you, not the point where you give up on it.
 */
export const LEECH_LAPSES = 4;

/** Score boundaries between the three bands. */
const EASY_CEILING = 0.35;
const HARD_FLOOR = 0.65;

/**
 * How much trouble a card is giving you: a 0–1 score and the band it falls in.
 *
 * FSRS has no ease factor. SM-2's single number was replaced by two: `stability`
 * (how many days the memory lasts) and `difficulty` (1–10, how hard the card is
 * intrinsically, and note it runs the opposite way to Anki's ease). Stability is
 * the wrong one to surface here — it is essentially the interval the row already
 * prints beside this, so a lamp driven by it would say the same thing twice.
 *
 * So the lamp reads the worse of two things: what the scheduler makes of the
 * card, and how often you have actually forgotten it. Taking the max rather
 * than a weighted blend is the load-bearing choice — `lapses` is 0 for every
 * card until a library has been reviewed for a while, and a blend would let a
 * structurally-zero term compress the whole scale toward "easy" for months.
 *
 * A card you have never answered has neither number behind it, so it gets
 * `new` and no colour rather than a fabricated one.
 */
export const getCardDifficulty = (card) => {
  if (!card || !(card.reps > 0)) return { score: 0, level: 'new' };

  const difficulty = Math.min(Math.max(Number(card.difficulty) || 1, 1), 10);
  const scheduler = (difficulty - 1) / 9;
  const forgetting = Math.min(1, (Number(card.lapses) || 0) / LEECH_LAPSES);
  const score = Math.max(scheduler, forgetting);

  if (score < EASY_CEILING) return { score, level: 'easy' };
  if (score < HARD_FLOOR) return { score, level: 'moderate' };
  return { score, level: 'hard' };
};

/**
 * Check if a card is due for review
 * @param {object} card - Card object
 * @param {Date} now - Current date
 * @returns {boolean}
 */
export const isCardDue = (card, now = new Date()) => {
  return new Date(card.due) <= now;
};

/**
 * Get cards due for review, respecting daily limits
 * @param {Array} cards - All cards in deck
 * @param {object} limits - { newCardsPerDay, reviewCardsPerDay }
 * @param {Date} now - Current date
 * @returns {Array} Cards to review
 */
export const getDueCards = (cards, limits = {}, now = new Date()) => {
  const { newCardsPerDay = 20, reviewCardsPerDay = 200 } = limits;
  
  const dueCards = cards.filter(card => isCardDue(card, now));
  
  // Separate new and review cards
  const newCards = dueCards.filter(c => c.state === State.New);
  const reviewCards = dueCards.filter(c => c.state !== State.New);
  
  // Apply limits
  const limitedNew = newCards.slice(0, newCardsPerDay);
  const limitedReview = reviewCards.slice(0, reviewCardsPerDay);
  
  // Interleave: new cards first, then reviews
  return [...limitedNew, ...limitedReview];
};

/**
 * Count cards due today
 * @param {Array} cards - All cards
 * @param {Date} now - Current date
 * @returns {number}
 */
export const countDueCards = (cards, now = new Date()) => {
  return cards.filter(card => isCardDue(card, now)).length;
};

